import { publicUrlStr } from '../utils';

// --- Types ---
export interface ICategory {
  id: string;
  name: string;
  icon: string | null;
  order: number;
}

export interface IVideo {
  id: string;
  title: string;
  source: string;
  yt: boolean;
  categoryId: string;
  order: number;
  createdAt: string;
}

export interface IImage {
  id: string;
  title: string;
  source: string;
  categoryId: string;
  order: number;
  createdAt: string;
}

export interface IAudio {
  id: string;
  title: string;
  source: string;
  categoryId: string;
  order: number;
  createdAt: string;
}

export interface IYoutubeFeed {
  id: string;
  title: string;
  source: string;
  yt: boolean;
  categoryId: string;
  order: number;
  createdAt: string;
}

export interface IContentData {
  categories: ICategory[];
  videos: IVideo[];
  images: IImage[];
  audios: IAudio[];
  youtubeFeeds: IYoutubeFeed[];
}

// --- Grouped types for pages ---
export interface ICategoryGroup<T> {
  category: ICategory;
  items: T[];
}

// --- Fetch ---
let cachedData: IContentData | null = null;

export async function fetchContentData(): Promise<IContentData> {
  if (cachedData) return cachedData;

  const response = await fetch(`${publicUrlStr()}/content-data.json`);
  if (!response.ok) throw new Error('Failed to load content data');
  cachedData = await response.json();
  return cachedData!;
}

export function invalidateCache() {
  cachedData = null;
}

// --- Helpers ---
function sortByOrder<T extends { order: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.order - b.order);
}

export function getVideosByCategory(data: IContentData): ICategoryGroup<IVideo>[] {
  const cats = sortByOrder(data.categories);
  return cats
    .map(cat => ({
      category: cat,
      items: sortByOrder(data.videos.filter(v => v.categoryId === cat.id))
    }))
    .filter(g => g.items.length > 0);
}

export function getImagesByCategory(data: IContentData): ICategoryGroup<IImage>[] {
  const cats = sortByOrder(data.categories);
  return cats
    .map(cat => ({
      category: cat,
      items: sortByOrder(data.images.filter(i => i.categoryId === cat.id))
    }))
    .filter(g => g.items.length > 0);
}

export function getAudiosByCategory(data: IContentData): ICategoryGroup<IAudio>[] {
  const cats = sortByOrder(data.categories);
  return cats
    .map(cat => ({
      category: cat,
      items: sortByOrder(data.audios.filter(a => a.categoryId === cat.id))
    }))
    .filter(g => g.items.length > 0);
}

export function getYoutubeFeeds(data: IContentData): IYoutubeFeed[] {
  return sortByOrder(data.youtubeFeeds);
}

export function getAllMediaFlat(data: IContentData) {
  const items: { title: string; source: string; type: 'video' | 'image' | 'audio'; category: string; yt?: boolean }[] = [];

  const catMap = new Map(data.categories.map(c => [c.id, c.name]));

  data.videos.forEach(v => items.push({ title: v.title, source: v.source, type: 'video', category: catMap.get(v.categoryId) || '기타', yt: v.yt }));
  data.images.forEach(i => items.push({ title: i.title, source: i.source, type: 'image', category: catMap.get(i.categoryId) || '기타' }));
  data.audios.forEach(a => items.push({ title: a.title, source: a.source, type: 'audio', category: catMap.get(a.categoryId) || '기타' }));
  data.youtubeFeeds.forEach(y => items.push({ title: y.title, source: y.source, type: 'video', category: catMap.get(y.categoryId) || '기타', yt: y.yt }));

  return items;
}

// --- Admin auth token ---
const ADMIN_TOKEN = 'gdflix-admin-2025';

function adminHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'x-admin-token': ADMIN_TOKEN,
  };
}

// --- CRUD operations (save back to content-data.json via proxy) ---
export async function saveContentData(data: IContentData): Promise<void> {
  const response = await fetch('/api/admin/content', {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Save failed' }));
    throw new Error(err.error || 'Failed to save content data');
  }
  cachedData = data;
}

// --- Report video generation ---
export async function generateReportVideo(form: {
  title: string; content: string; author: string; style: string; provider: string;
}): Promise<any> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 120000); // 2min timeout

  try {
    const response = await fetch('/api/admin/generate-report-video', {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify(form),
      signal: controller.signal,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Generation failed' }));
      throw new Error(err.error || 'Generation failed');
    }
    const data = await response.json();
    return data.script;
  } finally {
    clearTimeout(timeout);
  }
}

// --- ID generation ---
export function generateId(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}`;
}
